package com.example.event_management_system.ui.components.calendar

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.VerticalDivider
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.example.event_management_system.data.models.Event
import com.example.event_management_system.data.models.EventStatus
import com.example.event_management_system.ui.theme.Dimens
import com.example.event_management_system.ui.theme.statusColor
import com.example.event_management_system.util.currentLocalDate
import kotlinx.datetime.DateTimeUnit
import kotlin.time.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.plus
import kotlinx.datetime.toLocalDateTime

@Composable
fun CalendarWeekView(
    anchorDate: LocalDate,
    events: List<Event>,
    onEventClick: (Event) -> Unit,
    onDayClick: (LocalDate) -> Unit,
    modifier: Modifier = Modifier
) {
    val today = currentLocalDate()
    val daysFromSunday = (anchorDate.dayOfWeek.ordinal + 1) % 7
    val weekStart = anchorDate.plus(-daysFromSunday, DateTimeUnit.DAY)
    val weekDays = (0..6).map { weekStart.plus(it, DateTimeUnit.DAY) }

    Column(modifier = modifier.fillMaxSize()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(vertical = Dimens.spacingXSmall)
        ) {
            weekDays.forEach { day ->
                val isToday = day == today
                Column(
                    modifier = Modifier.weight(1f),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Text(
                        text = day.dayOfWeek.name.take(3).lowercase().replaceFirstChar { it.uppercase() },
                        style = MaterialTheme.typography.labelSmall,
                        color = if (isToday) MaterialTheme.colorScheme.primary
                                else MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    if (isToday) {
                        Box(
                            modifier = Modifier
                                .size(Dimens.calendarDaySize)
                                .clip(CircleShape)
                                .background(MaterialTheme.colorScheme.primary),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                text = day.dayOfMonth.toString(),
                                style = MaterialTheme.typography.labelMedium,
                                color = MaterialTheme.colorScheme.onPrimary
                            )
                        }
                    } else {
                        Text(
                            text = day.dayOfMonth.toString(),
                            style = MaterialTheme.typography.labelMedium,
                            textAlign = TextAlign.Center
                        )
                    }
                }
            }
        }

        HorizontalDivider()

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
        ) {
            weekDays.forEachIndexed { index, day ->
                if (index > 0) VerticalDivider()
                val dayEvents = events.filter { event ->
                    Instant.fromEpochMilliseconds(event.eventDate)
                        .toLocalDateTime(TimeZone.UTC).date == day
                }
                Column(
                    modifier = Modifier
                        .weight(1f)
                        .fillMaxHeight()
                        .clickable { onDayClick(day) }
                        .padding(top = Dimens.spacingXxSmall),
                    verticalArrangement = Arrangement.spacedBy(Dimens.spacingXSmall)
                ) {
                    dayEvents.take(3).forEach { event ->
                        WeekEventBar(event = event, onClick = { onEventClick(event) })
                    }
                    if (dayEvents.size > 3) {
                        Text(
                            text = "+${dayEvents.size - 3}",
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.primary,
                            modifier = Modifier.padding(start = 2.dp)
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun WeekEventBar(event: Event, onClick: () -> Unit) {
    val statusColor = event.status.statusColor
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = Dimens.spacingXSmall)
            .clip(RoundedCornerShape(Dimens.cornerRadiusSmall))
            .background(statusColor.copy(alpha = 0.25f))
            .clickable(onClick = onClick)
            .padding(horizontal = Dimens.spacingSmall, vertical = Dimens.spacingSmall),
        verticalAlignment = Alignment.Top
    ) {
        Box(
            modifier = Modifier
                .padding(top = 3.dp)
                .size(8.dp)
                .clip(CircleShape)
                .background(statusColor)
        )
        Spacer(Modifier.width(Dimens.spacingXSmall))
        Column {
            Text(
                text = event.customerName,
                style = MaterialTheme.typography.bodySmall,
                fontWeight = androidx.compose.ui.text.font.FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
                color = MaterialTheme.colorScheme.onSurface
            )
            Row {
                if (event.eventTime.isNotBlank()) {
                    Text(
                        text = event.eventTime,
                        style = MaterialTheme.typography.labelSmall,
                        maxLines = 1,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(Modifier.width(Dimens.spacingXSmall))
                }
                Text(
                    text = "· ${event.guestCount} guests",
                    style = MaterialTheme.typography.labelSmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
    }
}
